import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { BRANDS } from "../../data/whitespaces";
import { Reveal, MaskLine } from "../../lib/motionPrimitives";
import EmblemWatermark from "../EmblemWatermark";

const openPersonaCase = (index) => {
  window.dispatchEvent(new CustomEvent("whitespaces:open-persona", { detail: { index } }));
};

const BrandSlot = ({ brand, i }) => {
  const clickable = brand.personaIndex != null;
  const Tag = clickable ? "button" : "div";

  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={clickable ? () => openPersonaCase(brand.personaIndex) : undefined}
      data-testid={`brand-logo-${brand.slug}`}
      className={`group relative flex h-16 w-[150px] shrink-0 items-center justify-center px-3 md:h-20 md:w-[175px] ${
        clickable ? "cursor-pointer" : ""
      }`}
    >
      <img
        src={brand.logo}
        alt={brand.name}
        loading={i < 4 ? "eager" : "lazy"}
        onError={(e) => {
          e.currentTarget.style.display = "none";
          if (e.currentTarget.nextElementSibling) {
            e.currentTarget.nextElementSibling.style.display = "block";
          }
        }}
        className={`max-h-full max-w-full object-contain transition-transform duration-300 ${
          clickable ? "group-hover:scale-[1.08]" : ""
        }`}
      />
      <span className="hidden font-body text-[11px] uppercase tracking-[0.15em] text-black/50">
        {brand.name}
      </span>
      {clickable && (
        <span className="pointer-events-none absolute -bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black px-2.5 py-1 font-body text-[9px] uppercase tracking-[0.15em] text-teal opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          View case study
        </span>
      )}
    </Tag>
  );
};

export const BrandLogos = () => {
  return (
    <section
      id="brands"
      data-testid="brand-logos-section"
      className="relative border-t border-white/5 py-14 md:py-20"
    >
      <EmblemWatermark className="-left-16 -top-10 h-[34vw] w-[34vw] max-w-[480px] -rotate-12" />
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="mb-8 flex items-center gap-4">
          <span className="font-body text-[11px] uppercase tracking-[0.3em] text-teal">01 — Proof</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-body text-[11px] uppercase tracking-[0.3em] text-white/30">Whitespaces</span>
        </div>

        <h2 className="max-w-4xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-white md:text-6xl lg:text-7xl">
          <MaskLine className="pb-[0.08em]">Trusted by</MaskLine>
          <MaskLine delay={0.08} className="pb-[0.08em] text-white/40">
            category-defining brands.
          </MaskLine>
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-xl font-body text-base leading-relaxed text-white/55">
            From esports launches to premium real estate, regional streaming to quick commerce —
            these are the brands activating Whitespaces' 69.1M+ top-of-funnel reservoir. Logos with a
            teal glow open the full case study.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.2}>
        <div className="relative mt-10 px-6 md:mt-12 md:px-12">
          {/* ambient glow ring behind the glass */}
          <div className="pointer-events-none absolute -inset-x-4 -inset-y-6 rounded-[3rem] bg-gradient-to-r from-teal/25 via-transparent to-ember/25 opacity-70 blur-3xl md:-inset-x-8" />

          <div
            className="relative overflow-hidden rounded-[2rem] border border-white/40 bg-white/[0.14] backdrop-blur-2xl md:rounded-[2.5rem]"
            style={{ boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.5), 0 30px 90px -25px rgba(0,0,0,0.7)" }}
            data-testid="brand-marquee-strip"
          >
            {/* liquid sheen sweeping across the glass */}
            <motion.div
              className="pointer-events-none absolute inset-y-0 left-0 z-20 w-1/3 -skew-x-[18deg] bg-gradient-to-r from-transparent via-white/35 to-transparent"
              animate={{ x: ["-140%", "440%"] }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear", repeatDelay: 1.2 }}
            />
            {/* frosted backing so logo artwork of any color stays legible */}
            <div className="absolute inset-0 bg-white/65" />

            <div className="relative z-10 py-10 md:py-14">
              <Marquee speed={55} gradient={false} autoFill pauseOnHover>
                {BRANDS.map((brand, i) => (
                  <BrandSlot key={brand.slug} brand={brand} i={i} />
                ))}
              </Marquee>
              <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-16 bg-gradient-to-r from-white/65 to-transparent md:w-32" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-16 bg-gradient-to-l from-white/65 to-transparent md:w-32" />
            </div>

            {/* top glass highlight */}
            <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-1/2 bg-gradient-to-b from-white/40 to-transparent" />
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default BrandLogos;
